import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { HOST } from "../../common/constant";
import axiosInstance from "../../common/interceptor";
import { toastOptions } from "../../common/Utils";
import "./AsphaltDashboard.css";

/**
 * ダッシュボード設定画面。メニューごとに別画面で1設定のみ表示する（SKY-56 / SKY-57）。
 *  - URL クエリ ?type=work → 作業開始日時設定 のみ
 *  - URL クエリ ?type=date → 日付表示設定 のみ（既定は work）
 * 保存は編集した項目のみ更新し、他の設定値は読込値(keep)を送って保持する
 * （DAT_ETC の他キーを空文字で上書き消去しないため）。作業開始日時は
 * "daily|08:00" もしくは "range|2026/06/24 00:00|2026/06/24 23:59" で保存。
 */
/**
 * Sky72: 保存形式 "YYYY/MM/DD HH:mm" → <input type="datetime-local"> の "YYYY-MM-DDTHH:mm"。
 * 解釈できない値は空にする（不正な保存値をそのまま編集させない）。
 */
const toInputValue = (s?: string): string => {
  const m = (s || "").trim().match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})[ T](\d{1,2}):(\d{2})/);
  if (!m) return "";
  const p = (n: string) => n.padStart(2, "0");
  return `${m[1]}-${p(m[2])}-${p(m[3])}T${p(m[4])}:${m[5]}`;
};

/** Sky72: 逆変換。"YYYY-MM-DDTHH:mm" → "YYYY/MM/DD HH:mm"（既存の保存形式を維持する）。 */
const fromInputValue = (s: string): string => (s ? s.replace("T", " ").replace(/-/g, "/") : "");

const DashboardSettings: React.FC = () => {
  const isWork = new URLSearchParams(window.location.search).get("type") !== "date";

  const [mode, setMode] = useState<"daily" | "range">("daily");
  const [dailyTime, setDailyTime] = useState("08:00");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [dateFormat, setDateFormat] = useState("time");
  // 編集しない設定値を保持（保存時に上書き消去しないため）。
  const [keep, setKeep] = useState<{ workStart: string; dateFormat: string; displayItems: string; displayOrder: string }>(
    { workStart: "", dateFormat: "time", displayItems: "", displayOrder: "" });

  useEffect(() => {
    axiosInstance
      .get(HOST() + "/Form/V1/Dat/AsphaltDashboard/Setting")
      .then((res) => {
        const d = res.data?.data as
          { workStart?: string; dateFormat?: string; displayItems?: string; displayOrder?: string } | undefined;
        const ws = d?.workStart ?? "";
        const df = d?.dateFormat ?? "time";
        // SKY-77: displayOrder も keep に含める（この画面から保存したときに並び順が消えるのを防ぐ）。
        setKeep({ workStart: ws, dateFormat: df, displayItems: d?.displayItems ?? "", displayOrder: d?.displayOrder ?? "" });
        setDateFormat(df);
        const parts = ws.split("|");
        // Sky72: 保存値をカレンダーコントロールが扱える形式に変換して復元する。
        if (parts[0] === "range") { setMode("range"); setRangeStart(toInputValue(parts[1])); setRangeEnd(toInputValue(parts[2])); }
        else if (parts[0] === "daily") { setMode("daily"); setDailyTime(parts[1] ?? "08:00"); }
      })
      .catch(() => toast.error("データの取得に失敗しました。", toastOptions));
  }, []);

  const save = () => {
    // Sky72: 範囲モードは未入力・逆転を保存させない。
    //   従来は自由入力のテキストだったため、解釈できない文字列でも保存でき、
    //   サーバ側の日時パースが失敗して「作業時間の絞り込みが黙って無効になる」不具合になっていた。
    if (isWork && mode === "range") {
      if (!rangeStart || !rangeEnd) {
        toast.error("開始日時と終了日時を入力してください。", toastOptions);
        return;
      }
      if (rangeStart >= rangeEnd) {
        toast.error("終了日時は開始日時より後にしてください。", toastOptions);
        return;
      }
    }
    // 編集した項目のみ差し替え、それ以外は読込値(keep)を送って保持する。
    const workStart = isWork
      ? (mode === "range" ? `range|${fromInputValue(rangeStart)}|${fromInputValue(rangeEnd)}` : `daily|${dailyTime}`)
      : keep.workStart;
    const payload = {
      workStart,
      dateFormat: isWork ? keep.dateFormat : dateFormat,
      displayItems: keep.displayItems,
      displayOrder: keep.displayOrder,
    };
    axiosInstance
      .put(HOST() + "/Form/V1/Dat/AsphaltDashboard/Setting", payload)
      .then(() => toast.success("更新に成功。", toastOptions))
      .catch(() => toast.error("更新に失敗。", toastOptions));
  };

  return (
    <div className="asp-dashboard">
      <div className="asp-devhead">
        <div className="asp-title" style={{ flex: 1 }}>{isWork ? "作業開始日時設定" : "日付表示設定"}</div>
        <div>
          <button type="button" className="btn btn-primary btn-sm mar-5" onClick={() => (window.location.href = "/dashboard")}>キャンセル</button>
          <button type="button" className="btn btn-primary btn-sm mar-5" onClick={save}>保存</button>
        </div>
      </div>

      {isWork ? (
        <table className="tb-border w-75">
          <tbody>
            <tr>
              <td className="p-2" style={{ width: "10em" }}>
                <label><input type="radio" className="mr-2" checked={mode === "daily"} onChange={() => setMode("daily")} />24時間未満</label>
              </td>
              <td className="p-2">
                毎日 <input type="time" className="form-control d-inline-block" style={{ width: "120px" }} value={dailyTime} onChange={(e) => setDailyTime(e.target.value)} disabled={mode !== "daily"} /> 開始
                <span className="ml-2" style={{ fontSize: 12 }}>※毎日同じ時刻に作業を開始します。</span>
              </td>
            </tr>
            <tr>
              <td className="p-2">
                <label><input type="radio" className="mr-2" checked={mode === "range"} onChange={() => setMode("range")} />24時間以上</label>
              </td>
              <td className="p-2">
                {/* Sky72: 自由入力のテキストではなくカレンダーコントロールで選択させる
                    （テキストだと解釈できない日時が保存され、作業時間の絞り込みが黙って無効になっていた）。 */}
                <input type="datetime-local" className="form-control d-inline-block" style={{ width: "220px" }} value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} disabled={mode !== "range"} />
                {" ～ "}
                <input type="datetime-local" className="form-control d-inline-block" style={{ width: "220px" }} value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} disabled={mode !== "range"} />
                <span className="ml-2" style={{ fontSize: 12 }}>※開始日時から終了日時までを作業時間として設定します。</span>
              </td>
            </tr>
          </tbody>
        </table>
      ) : (
        <table className="tb-border w-75">
          <tbody>
            <tr>
              <td className="p-2" style={{ width: "16em" }}>
                <label><input type="radio" name="df" className="mr-2" checked={dateFormat === "time"} onChange={() => setDateFormat("time")} />時刻のみ表示</label>
              </td>
              <td className="p-2"><span style={{ fontSize: 12 }}>※ダッシュボードの表示を時刻のみにします。</span></td>
            </tr>
            <tr>
              <td className="p-2">
                <label><input type="radio" name="df" className="mr-2" checked={dateFormat === "datetime"} onChange={() => setDateFormat("datetime")} />年月日と時刻を表示</label>
              </td>
              <td className="p-2"><span style={{ fontSize: 12 }}>※ダッシュボードの表示を年月日と時刻にします。</span></td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
};

export default DashboardSettings;
